/**
 * The studies list's URL state — the one place the board's filters are parsed, applied and
 * remembered. Filters live in the URL (`/admin/studies?org=&q=&archived=1`) so reload, new tabs,
 * shared links and browser back all reproduce the same list without any hidden state.
 *
 * `org` is a *selector* (which organization's studies to show), never a grant: the backend decides
 * what the viewer may see. Members only ever get their own org's studies, so for them it is moot.
 *
 * Two lists use it: the workspace board (`/studies`, the current workspace only — search and
 * archived) and LATO staff's all-organization board in Admin (`/admin/studies`, plus org).
 *
 * "Back to Studies" from inside a study restores the filters this tab last chose for that list — a
 * per-tab convenience in sessionStorage. A fresh tab (or junk) falls back to the unfiltered list.
 */

import type { StudyListItem } from "@/features/studies/api";
import type { StudySurface } from "@/features/studies/studySurface";

export interface StudiesListSearch {
  org?: string;
  q?: string;
  archived?: true;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RETURN_KEYS: Record<StudySurface, string> = {
  workspace: "lato:studies-list-search",
  admin: "lato:admin-studies-list-search",
};

/** Keep only well-formed filters (TanStack `validateSearch`); unknown keys drop out of the URL —
 *  including the retired `status` filter of old bookmarks (the board's columns already group by
 *  status), so it can never keep filtering invisibly. */
export function parseStudiesListSearch(raw: Record<string, unknown>): StudiesListSearch {
  const out: StudiesListSearch = {};
  if (typeof raw.org === "string" && UUID_RE.test(raw.org)) out.org = raw.org.toLowerCase();
  if (typeof raw.q === "string" && raw.q.trim()) out.q = raw.q.trim();
  if (
    raw.archived === true ||
    raw.archived === "1" ||
    raw.archived === 1 ||
    raw.archived === "true"
  )
    out.archived = true;
  return out;
}

/** Route search middleware: TanStack carries unknown search keys into every location it builds,
 *  so the retired `status` param is removed here — navigations from a list never re-emit it. */
export function dropRetiredListParams<T extends object>({
  search,
  next,
}: {
  search: T;
  next: (search: T) => T;
}): T {
  const { status: _retired, ...rest } = next(search) as T & { status?: unknown };
  return rest as T;
}

type Filterable = StudyListItem & { org_name?: string; archived_at?: string | null };

/** Apply the URL filters to a loaded board (search matches title and organization name). */
export function filterBoardStudies<T extends Filterable>(
  studies: T[],
  search: StudiesListSearch
): T[] {
  const needle = search.q?.toLowerCase();
  return studies.filter(
    (s) =>
      (search.archived || s.archived_at == null) &&
      (!search.org || s.org_id === search.org) &&
      (!needle ||
        s.title.toLowerCase().includes(needle) ||
        (s.org_name?.toLowerCase().includes(needle) ?? false))
  );
}

/** The workspace list has no organization filter: it is the current workspace. */
export function workspaceListSearch(search: StudiesListSearch): StudiesListSearch {
  return {
    ...(search.q ? { q: search.q } : {}),
    ...(search.archived ? { archived: true as const } : {}),
  };
}

/** Remember the list this tab is looking at, so leaving a study returns to it. */
export function rememberStudiesListSearch(
  search: StudiesListSearch,
  surface: StudySurface = "workspace"
): void {
  try {
    sessionStorage.setItem(RETURN_KEYS[surface], JSON.stringify(search));
  } catch {
    // Storage blocked: Studies then opens the unfiltered list, which is still correct.
  }
}

/** The filters to return to (validated again — storage is untrusted input). */
export function studiesListReturnSearch(surface: StudySurface = "workspace"): StudiesListSearch {
  try {
    const raw = sessionStorage.getItem(RETURN_KEYS[surface]);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const search = parseStudiesListSearch(parsed as Record<string, unknown>);
    return surface === "admin" ? search : workspaceListSearch(search);
  } catch {
    return {};
  }
}
